import {
  AcceptChallengeError,
  ProfileChangeResultTypenames,
  ProfileChangeTypeInput,
} from '@varo/graphql-schema-types';
import { useLocalSearchParams, useRouter } from 'expo-router';
import useProfileChange from 'features/myVaro/hooks/useProfileChange';
import useAcceptChallengeGql from 'features/otp/hooks/useAcceptChallengeGql';
import React, { useEffect, useLayoutEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import { AbortError } from 'shared/hooks/useCancellableMutation';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { useAuth } from 'shared/providers/auth';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';
import { CombinedError } from 'urql';

const EmailLinkToken = () => {
  const router = useRouter();
  // Helper function for delay
  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));
  const { tokenRef } = useLocalSearchParams<{ tokenRef: string }>();
  const { acceptChallenge, responseError, apiError, isVerified, challengeId } =
    useAcceptChallengeGql();
  const { loginEvaluated } = useAuth();

  const { error: profileChangeError, profileChange } = useProfileChange();

  const errorObj = responseError || apiError;

  useLayoutEffect(() => {
    if (!loginEvaluated) {
      return;
    }

    const confirmToken = async () => {
      try {
        await acceptChallenge({
          input: {
            acceptOptions: {
              tokenOption: {
                tokenReference: tokenRef,
              },
            },
          },
        });
      } catch (error) {
        if (error instanceof AbortError) {
          return;
        }
      }
    };
    void confirmToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginEvaluated]);

  useEffect(() => {
    const throwError = (error?: AcceptChallengeError | CombinedError) => {
      logBreadcrumb({
        level: 'info',
        message: `Token not accepted: ${tokenRef}, Result: ${error && JSON.stringify(error)}}`,
      });
      router.replace({
        pathname: '/myvaro/personal-details',
        params: {
          refresh: 'true',
          errorKey: responseError
            ? 'phoneChangeFailed'
            : 'phoneChangeApiFailed',
          apiErrorObj: JSON.stringify(error),
        },
      });
    };

    const processResult = async () => {
      const response = await profileChange(
        { phoneNumberChangeRequest: { challengeId: challengeId } },
        ProfileChangeTypeInput.PhoneNumberChange,
      );
      const phoneChangeResult = response.data?.profileChange;
      if (
        phoneChangeResult?.__typename ===
        ProfileChangeResultTypenames.ProfileChangeSuccess
      ) {
        const isPhoneChange = Boolean(phoneChangeResult.updated);
        if (isPhoneChange) {
          await delay(2000);
          router.replace({
            pathname: '/myvaro/personal-details',
            params: { refresh: 'true', msgKey: 'phoneChanged' },
          });
        } else {
          throwError(profileChangeError);
        }
      } else {
        throwError(profileChangeError);
      }
    };

    if (loginEvaluated && isVerified && challengeId) {
      void processResult();
    } else if (loginEvaluated && errorObj) {
      throwError(errorObj);
    }
    // Please dont add any dependencies in the dep array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVerified, errorObj, tokenRef, challengeId, loginEvaluated, router]);

  return (
    <View style={styles.container}>
      <ScreenTransitionOverlay
        isLoading={true}
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={null}
      />
    </View>
  );
};
const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default EmailLinkToken;
